import {
  pick, omitBy, isNil,
} from 'lodash'
import ThemeApi from '@/api/theme'
import theme, { DEFAULT_THEME_ATTRIBUTES } from '@/constant/theme'
import { isFeatureEnabled } from '@/vuex/modules/featureToggles.module'
import { normaliseThemeImages } from '@/utils/themeImage.utils'

const THEME_MANAGEMENT_V2_FLAG = 'AHA-47841-theme-management-v2'
const BRAND_KIT_FLAG = 'release-brand-kit'

const THEME_ATTRIBUTES = ['themeName', 'textColour', 'baseColour', 'visibility', 'backgroundImage', 'themeThumbnail', 'themeId', 'themeActive', 'colorPalette', 'logo', 'fontFamily', 'brandKitId', 'brandRevision']

// A theme that is shared with the team, or published/official, can already be
// applied to other people's presentations, so editing it would retroactively
// change those decks — only a private, unpublished theme may be edited.
export const isThemeEditable = (presentationTheme) => Boolean(presentationTheme)
  && !presentationTheme.isShareWithTeam
  && !presentationTheme.isOfficial

const _state = {
  themes: [],
  officialThemes: [],
  teamThemes: [],
  isCreating: false,
  previewingTheme: null,
}

const EMPTY_THEMES_VALUES = {
}

export const nullifiedThemeEmptyField = (slide) => {
  const nullifiedSlide = {
    ...slide,
    textColour: slide.textColour === '' ? null : slide.textColour,
    baseColour: slide.baseColour === '' ? null : slide.baseColour,
    visibility: slide.visibility === -1 ? null : slide.visibility,
  }

  return omitBy(nullifiedSlide, isNil)
}

const actions = {
  getThemesAction: async ({ commit }) => {
    const themes = await ThemeApi.getThemes()
    commit('setThemes', themes)
  },
  getOfficialThemesAction: async ({ commit }) => {
    const officialThemes = await ThemeApi.getOfficialThemes()
    commit('setOfficialThemes', officialThemes)
  },
  getTeamThemesAction: async ({ commit }) => {
    const teamThemes = await ThemeApi.getTeamThemes()
    commit('setTeamThemes', teamThemes)
  },
  createThemesAction: async ({ getters, commit }, payload) => {
    try {
      commit('setIsCreating', true)
      const createdTheme = await ThemeApi.createThemes(payload)
      commit('setThemes', [createdTheme, ...getters.themes])
      return createdTheme
    } finally {
      commit('setIsCreating', false)
    }
  },
  updateThemeAction: async ({ commit, dispatch }, { id, payload }) => {
    try {
      commit('setIsCreating', true)
      await ThemeApi.updateTheme(id, payload)
      // The PATCH returns 204 (no body) and image fields come back signed as objects,
      // so re-fetch rather than merge the raw payload keys into local state.
      await dispatch('getThemesAction')
    } finally {
      commit('setIsCreating', false)
    }
  },
  deleteThemeAction: async ({ getters, commit }, id) => {
    const remainThemes = getters.themes?.filter((_theme) => {
      return _theme.id !== id
    })
    commit('setThemes', remainThemes)
    await ThemeApi.deleteTheme(id)
  },
  setThemeAction: async ({ dispatch, rootGetters }, rawPayload) => {
    // Switching to a non-brand theme must clear the brand link the deck carried.
    const payload = isFeatureEnabled(rootGetters, BRAND_KIT_FLAG)
      ? { ...rawPayload, brandKitId: rawPayload.brandKitId ?? null, brandRevision: rawPayload.brandRevision ?? null }
      : rawPayload
    dispatch('setThemeLocallyAction', payload)
    const themeAttribute = pick(payload, THEME_ATTRIBUTES)

    await ThemeApi.updatePresentationTheme(payload.presentationId, {
      ...themeAttribute,
      backgroundImage: themeAttribute.backgroundImage?.name,
      logo: themeAttribute.logo?.name,
    })
  },
  setThemeLocallyAction: async ({ commit }, payload) => {
    const themeAttribute = pick(payload, THEME_ATTRIBUTES)
    Object.keys(themeAttribute).forEach((key) => {
      commit('presentationAttributeModule/updatePresentationAttribute', {
        attributeName: key,
        attributeValue: themeAttribute[key],
      }, { root: true })
    })
  },
  setPreviewThemeAction: async ({ commit }, selectedTheme) => {
    commit('setPreviewTheme', selectedTheme)
  },
  // Reorders "My Themes" to the given id sequence. Order lives only in the store:
  // the presentation-themes API has no order column/field yet, and its PATCH bumps
  // updatedAt (the field GET sorts by DESC), so persisting here would scramble the
  // list on reload. Server-side persistence is a backend dependency — see PR notes.
  reorderThemesAction: ({ commit, getters }, orderedThemeIds) => {
    const themeById = new Map(getters.themes.map((presentationTheme) => [presentationTheme.id, presentationTheme]))
    const reordered = orderedThemeIds
      .map((id) => themeById.get(id))
      .filter(Boolean)
    if (reordered.length !== getters.themes.length) return
    commit('setThemes', reordered)
  },
  toggleShareThemeAction: async ({ state, getters, commit }, { id, isShare }) => {
    const updateThemes = getters.themes?.map((_theme) => {
      if (_theme.id === id) _theme.isShareWithTeam = isShare
      return _theme
    })

    commit('setThemes', updateThemes)
    await ThemeApi.updateTheme(id, { isShareWithTeam: isShare })

    if (isShare) {
      const updateTheme = state.themes.find((_theme) => _theme.id === id)
      commit('setTeamThemes', state.teamThemes.concat(updateTheme))
    } else {
      const remainThemes = state.teamThemes.filter((_theme) => _theme.id !== id)
      commit('setTeamThemes', remainThemes)
    }
  },
}

const _getters = {
  canUseTheme: (_, __, ___, rootGetters) => {
    return isFeatureEnabled(rootGetters, 'release-theme')
  },
  currentPresentationTheme: (_, getters, rootState) => {
    const attribute = rootState.presentationAttributeModule.presentationAttribute
    const themeAttribute = pick(attribute, THEME_ATTRIBUTES)
    // Front-end stored themes
    // Should be remove when theme gallery and custom theme feature are 100% released
    const frontEndTheme = theme.presentations.find((t) => t.themeId === themeAttribute.themeId)
    const { previewingTheme } = getters
    if (frontEndTheme) {
      return {
        ...DEFAULT_THEME_ATTRIBUTES,
        ...frontEndTheme,
        ...previewingTheme,
      }
    }

    return {
      ...DEFAULT_THEME_ATTRIBUTES,
      ...themeAttribute,
      ...previewingTheme,
    }
  },
  isCreating: (state) => state.isCreating,
  themes: (state) => state.themes,
  // make another alias for themes
  themeList: (state, getters, rootState, rootGetters) => {
    if (!isFeatureEnabled(rootGetters, THEME_MANAGEMENT_V2_FLAG)) {
      return state.themes?.map((t) => omitBy(t, isNil))
    }
    return state.themes?.map((t) => normaliseThemeImages(omitBy(t, isNil)))
  },
  officialThemes: (state, getters, rootState, rootGetters) => {
    if (!isFeatureEnabled(rootGetters, THEME_MANAGEMENT_V2_FLAG)) {
      return state.officialThemes?.map((t) => omitBy(t, isNil))
    }
    return state.officialThemes?.map((t) => normaliseThemeImages(omitBy(t, isNil)))
  },
  allThemes: (state) => [...state.themes, ...state.officialThemes],
  teamThemes: (state, getters, rootState, rootGetters) => {
    if (!isFeatureEnabled(rootGetters, THEME_MANAGEMENT_V2_FLAG)) {
      return state.teamThemes
    }
    return state.teamThemes?.map((t) => normaliseThemeImages(omitBy(t, isNil)))
  },
  previewingTheme: (state) => state.previewingTheme,
  defaultThemeValues: () => EMPTY_THEMES_VALUES,

}

const mutations = {
  setThemes(state, payload) {
    state.themes = payload
  },
  setOfficialThemes(state, payload) {
    state.officialThemes = payload
  },
  setIsCreating(state, payload) {
    state.isCreating = payload
  },
  setTeamThemes(state, payload) {
    state.teamThemes = payload
  },
  setPreviewTheme(state, payload) {
    state.previewingTheme = payload
  },
}

export default {
  namespaced: true,
  state: _state,
  actions,
  mutations,
  getters: _getters,
}
